/**
 * Create / edit a place (uc-core CreateLocationDto / UpdateLocationDto). Validation mirrors the
 * DTO so the office sees the problem before the server does.
 */
import type { LocationBody } from '../api/store'

export interface LocationForm {
  level: string
  kind: string
  code: string
  name: string
  role: string
  capacityKg: number | null
  groupId: string
  fssaiLicenceNo: string
  /** YYYY-MM-DD */
  fssaiLicenceValidTo: string
  active: boolean
}

export type LocationFormErrors = Partial<Record<keyof LocationForm, string>>

export const emptyLocationForm = (patch: Partial<LocationForm> = {}): LocationForm => ({
  level: 'SITE',
  kind: '',
  code: '',
  name: '',
  role: '',
  capacityKg: null,
  groupId: '',
  fssaiLicenceNo: '',
  fssaiLicenceValidTo: '',
  active: true,
  ...patch,
})

const CODE = /^[A-Za-z0-9][A-Za-z0-9-]*$/
const DATE = /^\d{4}-\d{2}-\d{2}$/

/** The code as the server stores it (upper case, no spaces). */
export const normalizeLocationCode = (code: string) => code.replace(/\s+/g, '').toUpperCase()

function validDate(s: string): boolean {
  if (!DATE.test(s)) return false
  const d = new Date(`${s}T00:00:00Z`)
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === s
}

/** Problems with the form, by field. `mode` edit checks only what PATCH accepts. */
export function validateLocationForm(f: LocationForm, mode: 'create' | 'edit'): LocationFormErrors {
  const e: LocationFormErrors = {}
  const name = f.name.trim()
  if (!name) e.name = 'Required'
  else if (name.length > 80) e.name = 'At most 80 characters'
  if (mode === 'create') {
    const code = normalizeLocationCode(f.code)
    if (!code) e.code = 'Required'
    else if (code.length > 32) e.code = 'At most 32 characters'
    else if (!CODE.test(code)) e.code = 'Letters, digits and dashes only, starting with a letter or digit'
    if (f.level === 'SITE' && !f.kind) e.kind = 'Choose what kind of site this is'
  }
  if (f.capacityKg !== null && (!Number.isInteger(f.capacityKg) || f.capacityKg < 0)) e.capacityKg = 'Whole kg, 0 or more'
  if (f.fssaiLicenceNo.trim().length > 20) e.fssaiLicenceNo = 'At most 20 characters'
  if (f.fssaiLicenceValidTo && !validDate(f.fssaiLicenceValidTo)) e.fssaiLicenceValidTo = 'Not a date'
  return e
}

/** POST /locations body. Children inherit the site's kind, so only a SITE sends one. */
export function toCreateBody(f: LocationForm, parentId: string | null): LocationBody {
  const site = f.level === 'SITE'
  return {
    ...(site ? {} : { parentId: parentId ?? undefined }),
    level: f.level,
    ...(site ? { kind: f.kind } : {}),
    code: normalizeLocationCode(f.code),
    name: f.name.trim(),
    ...(f.role ? { role: f.role } : {}),
    ...(f.capacityKg !== null ? { capacityKg: f.capacityKg } : {}),
    ...(site && f.groupId ? { groupId: f.groupId } : {}),
    ...(site && f.fssaiLicenceNo.trim() ? { fssaiLicenceNo: f.fssaiLicenceNo.trim() } : {}),
    ...(site && f.fssaiLicenceValidTo ? { fssaiLicenceValidTo: f.fssaiLicenceValidTo } : {}),
  }
}

/**
 * PATCH /locations/:id body. Role and capacity cannot be cleared through the API, so an empty
 * field leaves them as they are. A site's FSSAI licence is sent when typed; emptying a field
 * that had a value (`saved`) clears it.
 */
export function toUpdateBody(
  f: LocationForm,
  isSite: boolean,
  saved: { fssaiLicenceNo?: string | null; fssaiLicenceValidTo?: string | null } = {},
): LocationBody {
  const licenceNo = f.fssaiLicenceNo.trim()
  return {
    name: f.name.trim(),
    ...(f.role ? { role: f.role } : {}),
    ...(f.capacityKg !== null ? { capacityKg: f.capacityKg } : {}),
    active: f.active,
    ...(isSite && licenceNo ? { fssaiLicenceNo: licenceNo } : {}),
    ...(isSite && !licenceNo && saved.fssaiLicenceNo ? { fssaiLicenceNo: null } : {}),
    ...(isSite && f.fssaiLicenceValidTo ? { fssaiLicenceValidTo: f.fssaiLicenceValidTo } : {}),
    ...(isSite && !f.fssaiLicenceValidTo && saved.fssaiLicenceValidTo ? { fssaiLicenceValidTo: null } : {}),
  }
}

/** Is a site's FSSAI licence past its valid-to date (YYYY-MM-DD, compared with today's date)? */
export function fssaiExpired(validTo: string | null, today: string): boolean {
  return Boolean(validTo) && validTo! < today
}
